import { useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import { X, CheckCircle2, Smartphone, IndianRupee } from "lucide-react";
import api from "@/lib/api";

// UPI payment panel — patient scans the QR (or the doctor's uploaded GPay QR) and pays directly.
export default function PaymentPanel({ tc, onPaid, onClose }) {
  const [busy, setBusy] = useState(false);
  if (!tc) return null;
  const pay = tc.payment || {};
  const fee = pay.fee || tc.fee || 0;
  const upiLink =
    pay.upi_id
      ? `upi://pay?pa=${encodeURIComponent(pay.upi_id)}&pn=${encodeURIComponent(tc.doctor_name || "Doctor")}&am=${fee}&cu=INR&tn=${encodeURIComponent("MDvia Consult " + String(tc.id).slice(0, 8))}`
      : "";

  const markPaid = async () => {
    setBusy(true);
    try {
      await api.post(`/teleconsults/${tc.id}/pay-claim`);
      toast.success("Thanks! We've told the doctor you've paid.");
      onPaid?.();
    } catch {
      toast.error("Could not update payment status");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[250] flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-sm" data-testid="payment-panel">
      <div className="w-full max-w-md rounded-3xl bg-white shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
          <h3 className="font-semibold text-slate-900">Pay consultation fee</h3>
          <button data-testid="payment-close" onClick={onClose} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 text-center">
          <p className="text-sm text-slate-500">Pay <b className="text-slate-800" data-notranslate>{/^dr\.?\s/i.test(tc.doctor_name || "") ? tc.doctor_name : "Dr. " + (tc.doctor_name || "Doctor")}</b></p>
          <p className="mt-1 inline-flex items-center justify-center text-3xl font-bold text-[#123C73]" data-notranslate><IndianRupee className="w-6 h-6" />{fee.toLocaleString("en-IN")}</p>

          {pay.upi_qr_image ? (
            <div className="mt-4">
              <img data-testid="doctor-qr-image" src={pay.upi_qr_image} alt="Scan to pay" className="mx-auto max-h-56 rounded-xl border border-slate-100" />
              <p className="text-xs text-slate-400 mt-2">Scan with any UPI app (GPay, PhonePe, Paytm)</p>
            </div>
          ) : upiLink ? (
            <div className="mt-4">
              <div className="inline-block p-3 bg-white rounded-xl border border-slate-100">
                <QRCodeCanvas data-testid="upi-qr" value={upiLink} size={190} includeMargin />
              </div>
              <p className="text-xs text-slate-400 mt-2 flex items-center justify-center gap-1"><Smartphone className="w-3.5 h-3.5" /> Scan with GPay / PhonePe / Paytm · {pay.upi_id}</p>
            </div>
          ) : (
            <div className="mt-4 rounded-xl bg-amber-50 border border-amber-100 p-4 text-sm text-amber-800">This doctor hasn't added payment details yet. You can pay at the time of consultation.</div>
          )}

          <button data-testid="btn-ive-paid" onClick={markPaid} disabled={busy}
            className="mt-6 w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold disabled:opacity-60">
            <CheckCircle2 className="w-5 h-5" /> I've paid
          </button>
          <p className="text-[11px] text-slate-400 mt-3">Your appointment is confirmed. The doctor will verify the payment before the call.</p>
        </div>
      </div>
    </div>
  );
}
